import { useEffect, useRef } from "react";
import { ensureReminderPushRegistration } from "../firebase";
import { Message } from "../types";

interface UsePushNotificationsOptions {
  roomCode: string | null;
  roomName?: string | null;
  currentUserUid?: string | null;
  messages: Message[];
}

const STORAGE_KEY = "BlinkBli:notificationPrompted";

/**
 * Lightweight client-side push notifications using the Web Notification API.
 * This keeps users informed about new chat messages whenever the tab/PWA
 * is hidden but still running.
 */
export function usePushNotifications({
  roomCode,
  roomName,
  currentUserUid,
  messages,
}: UsePushNotificationsOptions) {
  const lastMessageIdRef = useRef<string | null>(null);
  const requestedPermissionRef = useRef(false);
  const registeredTokenRef = useRef(false);

  // Ask for permission once the user opens a room (soft prompt)
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !("Notification" in window) ||
      !roomCode ||
      Notification.permission !== "default"
    ) {
      return;
    }

    const wasPrompted = localStorage.getItem(STORAGE_KEY) === "true";
    if (wasPrompted || requestedPermissionRef.current) {
      return;
    }

    requestedPermissionRef.current = true;

    const timeout = setTimeout(() => {
      Notification.requestPermission()
        .then(() => {
          localStorage.setItem(STORAGE_KEY, "true");
        })
        .catch((err) => {
          console.warn("Notification permission request failed:", err);
        });
    }, 1000);

    return () => clearTimeout(timeout);
  }, [roomCode]);

  // Register FCM token for scheduled reminders once permission granted
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !("Notification" in window) ||
      Notification.permission !== "granted" ||
      !currentUserUid
    ) {
      return;
    }
    if (registeredTokenRef.current) {
      return;
    }

    const deviceId =
      localStorage.getItem("BlinkBli:deviceId") || crypto.randomUUID();
    localStorage.setItem("BlinkBli:deviceId", deviceId);

    ensureReminderPushRegistration(deviceId)
      .then(() => {
        registeredTokenRef.current = true;
      })
      .catch((err) => {
        console.warn("Reminder push registration failed:", err);
        registeredTokenRef.current = false;
      });
  }, [currentUserUid, roomCode]);

  // Show notification when a new message arrives while the tab is hidden
  useEffect(() => {
    if (
      typeof window === "undefined" ||
      !("Notification" in window) ||
      Notification.permission !== "granted" ||
      !messages.length
    ) {
      return;
    }

    const lastMessage = messages[messages.length - 1];
    if (!lastMessage || lastMessage.senderUid === currentUserUid) {
      return;
    }

    if (lastMessageIdRef.current === lastMessage.id) {
      return;
    }
    lastMessageIdRef.current = lastMessage.id;

    if (document.visibilityState === "visible") {
      return;
    }

    const titleSuffix = roomName
      ? roomName
      : roomCode
      ? `Room ${roomCode}`
      : "BlinkBli";
    const title = `New message · ${titleSuffix}`;
    const body = `${lastMessage.senderName}: ${lastMessage.text}`.slice(0, 140);
    const tag = roomCode ? `BlinkBli-room-${roomCode}` : "BlinkBli-room";

    const showNotification = async () => {
      if ("serviceWorker" in navigator) {
        try {
          const registration = await navigator.serviceWorker.getRegistration();
          if (registration) {
            await registration.showNotification(title, {
              body,
              tag,
              data: {
                roomCode,
              },
            });
            return;
          }
        } catch (err) {
          console.warn("Service worker notification failed:", err);
        }
      }

      // Fallback to direct Notification API
      new Notification(title, {
        body,
        tag,
        data: {
          roomCode,
        },
      });
    };

    showNotification();
  }, [messages, currentUserUid, roomCode, roomName]);
}

